import { create } from 'zustand'

export const useGameStore = create((set, get) => ({
  // Player state
  player: {
    identity: null,
    recoveryKey: null,
    soulsHelped: 0,
    sessionId: null,
  },

  // Game state
  currentGame: null,
  gameState: 'idle', // 'idle', 'lobby', 'match_request_sent', 'match_request_received', 'in_game', 'success', 'fallen', 'try_again_request_sent', 'try_again_request_received', 'play_again_request_sent', 'play_again_request_received', 'victory', 'chat_request_sent', 'chat_request_received', 'chat'
  
  // UI state
  isLoading: false,
  error: null,
  explicitlyLeftLobby: false, // Track when user manually leaves lobby
  
  // Lobby state
  lobbyPlayers: [],
  
  // Match request state
  matchRequestData: null, // Stores current match request info
  
  // Current round state
  currentRound: {
    questions: [],
    timeRemaining: 45,
    answers: [],
    opponentIdentity: null,
    roundNumber: 0,
  },
  
  // Match results
  lastMatchResult: null,
  
  // Continue state
  continueState: {
    userClicked: false,
    partnerWaiting: false,
    waitingForPartner: false,
  },

  // Actions
  setPlayer: (player) => set({ player }),
  
  setPlayerIdentity: (identity, recoveryKey, soulsHelped = 0) => 
    set((state) => ({ 
      player: { 
        ...state.player, 
        identity, 
        recoveryKey, 
        soulsHelped 
      } 
    })),

  setPlayerSoulsHelped: (soulsHelped) =>
    set((state) => ({
      player: { ...state.player, soulsHelped }
    })),

  setGameState: (gameState) => set({ gameState }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  setExplicitlyLeftLobby: (left) => set({ explicitlyLeftLobby: left }),

  setLobbyPlayers: (players) => set({ lobbyPlayers: players }),

  setMatchRequestData: (data) => set({ matchRequestData: data }),

  setCurrentGame: (game) => set({ currentGame: game }),

  setCurrentRound: (round) => set({ currentRound: round }),

  updateTimeRemaining: (time) =>
    set((state) => ({
      currentRound: { ...state.currentRound, timeRemaining: time }
    })),

  addAnswer: (questionIndex, answer) =>
    set((state) => {
      const newAnswers = [...state.currentRound.answers]
      // Ensure array is large enough for the index
      while (newAnswers.length <= questionIndex) {
        newAnswers.push(null)
      }
      newAnswers[questionIndex] = answer
      return {
        currentRound: { ...state.currentRound, answers: newAnswers }
      }
    }),

  setMatchResult: (result) => set({ lastMatchResult: result }),

  setContinueState: (continueState) => set({ continueState }),
  
  resetContinueState: () => set({ 
    continueState: {
      userClicked: false,
      partnerWaiting: false,
      waitingForPartner: false,
    }
  }),

  // Reset functions
  resetGame: () => set({
    currentGame: null,
    gameState: 'idle',
    matchRequestData: null,
    explicitlyLeftLobby: false,
    currentRound: {
      questions: [],
      timeRemaining: 45,
      answers: [],
      opponentIdentity: null,
      roundNumber: 0,
    },
    lastMatchResult: null,
    continueState: {
      userClicked: false,
      partnerWaiting: false,
      waitingForPartner: false,
    },
  }),

  // Reset game state but preserve player identity (for returning to lobby)
  resetGameKeepIdentity: () => set({
    currentGame: null,
    gameState: 'lobby',  // Set directly to lobby
    matchRequestData: null,
    currentRound: {
      questions: [],
      timeRemaining: 45,
      answers: [],
      opponentIdentity: null,
      roundNumber: 0,
    },
    lastMatchResult: null,
    continueState: {
      userClicked: false,
      partnerWaiting: false,
      waitingForPartner: false,
    },
  }),

  resetPlayer: () => set({
    player: {
      identity: null,
      recoveryKey: null,
      soulsHelped: 0,
      sessionId: null,
    },
    gameState: 'idle',
    explicitlyLeftLobby: false,
  }),

  // Helper functions
  getPlayerProgress: () => {
    const { player } = get()
    const soulsHelped = player.soulsHelped || 0
    return {
      soulsHelped,
      // Remove level-based progress since levels are per-partnership
      isExperienced: soulsHelped >= 5,
    }
  },

  canStartMatchmaking: () => {
    const { player, gameState } = get()
    return player.identity && gameState === 'lobby'
  },

  // Persistence helpers
  saveToLocalStorage: () => {
    const { player } = get()
    if (player.recoveryKey) {
      localStorage.setItem('commonground_recovery', player.recoveryKey)
      localStorage.setItem('commonground_identity', player.identity)
      localStorage.setItem('commonground_souls_helped', player.soulsHelped.toString())
    }
  },

  loadFromLocalStorage: () => {
    const recoveryKey = localStorage.getItem('commonground_recovery')
    const identity = localStorage.getItem('commonground_identity')
    const soulsHelped = parseInt(localStorage.getItem('commonground_souls_helped')) || 0
    
    if (recoveryKey && identity) {
      set((state) => ({
        player: {
          ...state.player,
          recoveryKey,
          identity,
          soulsHelped,
        }
      }))
      return { recoveryKey, identity, soulsHelped }
    }
    return null
  },

  clearLocalStorage: () => {
    localStorage.removeItem('commonground_recovery')
    localStorage.removeItem('commonground_identity')
    localStorage.removeItem('commonground_souls_helped')
  },
}))
